import { Injectable } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { EmriNumri, EmriFormaFjales, EmriRasa, EmriTrajta } from '../enums/emri.enum';
import { EmriConstants } from '../globals/emri-constants';
import { EmriBazaForCreationDTO } from '../interfaces/dto/emri-baza-for-creation-dto.interface';
import { EmriBazaForUpdateDTO } from '../interfaces/dto/emri-baza-for-update-dto.interface';
import { EmriForCreationDTO } from '../interfaces/dto/emri-for-creation-dto.interface';
import { EmriBaza } from '../interfaces/models/emri-baza.interface';

@Injectable({
  providedIn: 'root',
})
export class EmriEditorService {
  constructor(private fb: UntypedFormBuilder) { }

  getEmriBazaForm(form: UntypedFormGroup) {
    if (form.get('emriBaza')) {
      return <UntypedFormGroup>form.get('emriBaza');
    }

    form.addControl(
      'emriBaza',
      this.fb.group({
        id: null,
        bazaId: null,
        gjiniaVecoriaId: [
          {
            value: null,
            disabled: true,
          },
        ],
        gjiniaNenvecoria1Id: null,
        njejesParam: true,
        shumesParam: true,
        lakimiId: null,
        tipiId: null,
        shumesiId: {
          value: null,
          disabled: true,
        },
        prapashtesaId: null,
        e1: null,
        e2: null,
        e3: null,
        e3a: null,
        e3b: null,
        emri: this.fb.array([]),
        llojiVecoriaId: null,
        llojiNenvecoria1Id: null,
        llojiNenvecoria2Id: null,
        llojiNenvecoria3Id: null,
        iPervecemParam: false,
        iNyjshemParam: false,
        inputiManual: new UntypedFormControl(null),
        initialEmri: this.fb.array([]),
      })
    );

    this.generateEmriForms(form.get('emriBaza.emri') as UntypedFormArray);
    this.generateEmriForms(form.get('emriBaza.initialEmri') as UntypedFormArray);

    return <UntypedFormGroup>form.get('emriBaza')!;
  }

  getEmriBaza(form: UntypedFormGroup) {
    return form.get('emriBaza') as UntypedFormGroup;
  }

  generateEmriForms(formArray: UntypedFormArray) {
    while (formArray.length > 0) {
      formArray.removeAt(0);
    }

    for (const en of EmriConstants.EmriNumri) {
      for (const et of EmriConstants.EmriTrajta) {
        for (const er of EmriConstants.EmriRasa) {
          const emriForm = this.fb.group({
            rasaId: er.id,
            trajtaId: et.id,
            numriId: en.id,
            formaFjalesId: null,
            pershkrimi: '',
            renditja: 1,
          });

          formArray.push(emriForm);

          if (er.id == EmriRasa.Emerore && et.id == EmriTrajta.Epashquar) {
            const emriForm = this.fb.group({
              rasaId: er.id,
              trajtaId: et.id,
              numriId: en.id,
              formaFjalesId: EmriFormaFjales.cc,
              pershkrimi: '',
              renditja: 1,
            });
            formArray.push(emriForm);
          }
        }
      }
    }
  }

  addNewLakimi(
    formArray: UntypedFormArray,
    numri: EmriNumri,
    trajta: EmriTrajta,
    rasa: EmriRasa,
    formaFjales: EmriFormaFjales | null,
    pershkrimi?: string,
    renditja?: number
  ) {
    const renditjet = formArray.value
      .filter((c: any) => c.numriId == numri && c.trajtaId == trajta && c.rasaId == rasa && c.formaFjalesId == formaFjales)
      .map((c: any) => c.renditja);
    const prevIndex = Math.max(...(renditjet.length > 0 ? renditjet : [0]));
    const emriForm = this.fb.group({
      rasaId: rasa,
      trajtaId: trajta,
      numriId: numri,
      formaFjalesId: formaFjales,
      pershkrimi: pershkrimi,
      renditja: renditja ?? prevIndex + 1,
    });
    formArray.push(emriForm);
  }

  getEmriBazaDTO(form: UntypedFormGroup) {
    const eb = this.getEmriBazaForm(form).getRawValue();
    const emriForCreationDTO: EmriForCreationDTO[] = [];

    const formArray = eb.emri;

    for (let i = 0; i < formArray.length; i++) {
      const e = formArray[i];

      if (
        (!eb.njejesParam && e.numriId == EmriNumri.Njejes) ||
        (!eb.shumesParam && e.numriId == EmriNumri.Shumes) ||
        (eb.iNyjshemParam && e.formaFjalesId != null)
      )
        continue;

      emriForCreationDTO.push({
        numriId: e.numriId,
        rasaId: e.rasaId,
        trajtaId: e.trajtaId,
        formaFjalesId: e.formaFjalesId,
        pershkrimi: e.pershkrimi,
        renditja: e.renditja,
      });
    }

    if (eb.id) {
      const emriBazaForUpdateDTO: EmriBazaForUpdateDTO = {
        id: eb.id,
        bazaId: eb.bazaId,
        gjiniaVecoriaId: eb.gjiniaVecoriaId,
        lakimiId: eb.lakimiId,
        tipiId: eb.tipiId,
        shumesiId: eb.shumesiId,
        prapashtesaId: eb.prapashtesaId,
        emriForCreationDTO: emriForCreationDTO,
        llojiVecoriaId: eb.llojiVecoriaId,
        njejesParam: eb.njejesParam,
        shumesParam: eb.shumesParam,
        iPervecemParam: eb.iPervecemParam,
        iNyjshemParam: eb.iNyjshemParam,
        e1: eb.e1,
        e2: eb.e2,
        e3: eb.e3,
        e3a: eb.e3a,
        e3b: eb.e3b,
      };
      return emriBazaForUpdateDTO;
    } else {
      const emriBazaForCreationDTO: EmriBazaForCreationDTO = {
        id: eb.id,
        bazaId: eb.bazaId,
        gjiniaVecoriaId: eb.gjiniaVecoriaId,
        lakimiId: eb.lakimiId,
        tipiId: eb.tipiId,
        shumesiId: eb.shumesiId,
        prapashtesaId: eb.prapashtesaId,
        emriForCreationDTO: emriForCreationDTO,
        llojiVecoriaId: eb.llojiVecoriaId,
        njejesParam: eb.njejesParam,
        shumesParam: eb.shumesParam,
        iPervecemParam: eb.iPervecemParam,
        iNyjshemParam: eb.iNyjshemParam,
        e1: eb.e1,
        e2: eb.e2,
        e3: eb.e3,
        e3a: eb.e3a,
        e3b: eb.e3b,
      };
      return emriBazaForCreationDTO;
    }
  }

  insertEmriBazaIntoEditor(emriBaza: EmriBaza, form: UntypedFormGroup) {
    this.resetEmriBaza(form);

    this.getEmriBazaForm(form).patchValue({
      id: emriBaza.id,
      bazaId: emriBaza.bazaId,
      gjiniaVecoriaId: emriBaza.gjiniaVecoriaId,
      gjiniaNenvecoria1Id: emriBaza.gjiniaNenvecoria1Id,
      njejesParam: emriBaza.prapashtesaId == null ? true : emriBaza.njejesParam,
      shumesParam: emriBaza.prapashtesaId == null ? true : emriBaza.shumesParam,
      lakimiId: emriBaza.lakimiId,
      tipiId: emriBaza.tipiId,
      shumesiId: emriBaza.shumesiId,
      prapashtesaId: emriBaza.prapashtesaId,
      llojiVecoriaId: emriBaza.llojiVecoriaId,
      llojiNenvecoria1Id: emriBaza.llojiNenvecoria1Id,
      llojiNenvecoria2Id: emriBaza.llojiNenvecoria2Id,
      llojiNenvecoria3Id: emriBaza.llojiNenvecoria3Id,
      iPervecemParam: emriBaza.iPervecemParam,
      iNyjshemParam: emriBaza.iNyjshemParam,
      e1: emriBaza.e1,
      e2: emriBaza.e2,
      e3: emriBaza.e3,
      e3a: emriBaza.e3a,
      e3b: emriBaza.e3b,
    });

    let formArrays = ['initialEmri', 'emri'];

    for (let i = 0; i < formArrays.length; i++) {
      const formArray = this.getEmriBazaForm(form).get(formArrays[i]) as UntypedFormArray;
      this.generateEmriForms(formArray);

      this.insertEmriIntoFormArray(formArray, emriBaza.emri);
    }
  }

  insertEmriIntoFormArray(formArray: UntypedFormArray, emri: any) {
    emri?.forEach((item: any) => {
      let index = formArray.controls.findIndex(
        (c) =>
          c.get('numriId')?.value == item.numriId &&
          c.get('trajtaId')?.value == item.trajtaId &&
          c.get('rasaId')?.value == item.rasaId &&
          c.get('formaFjalesId')?.value == item.formaFjalesId &&
          c.get('pershkrimi')?.value == '' &&
          c.get('renditja')?.value == item.renditja
      );
      if (index != -1) {
        formArray.controls[index].get('pershkrimi')?.setValue(item.pershkrimi);
      } else {
        this.addNewLakimi(formArray, item.numriId, item.trajtaId, item.rasaId, item.formaFjalesId, item.pershkrimi, item.renditja);
      }
    });
  }

  private resetEmriBaza(form: UntypedFormGroup) {
    this.getEmriBazaForm(form).reset({
      id: null,
      bazaId: null,
      gjiniaVecoriaId: null,
      gjiniaNenvecoria1Id: null,
      njejesParam: true,
      shumesParam: true,
      lakimiId: null,
      tipiId: null,
      shumesiId: null,
      prapashtesaId: null,
      llojiVecoriaId: null,
      llojiNenvecoria1Id: null,
      llojiNenvecoria2Id: null,
      llojiNenvecoria3Id: null,
      iPervecemParam: false,
      iNyjshemParam: false,
      inputiManual: null,
    });

    Object.keys(this.getEmriBazaForm(form).controls)
      .filter((c) => c.toLowerCase().includes('emri'))
      .flatMap((field) => {
        return this.getEmriBazaForm(form).get(field) as UntypedFormArray;
      })
      .forEach((formArray) => {
        while (formArray.controls.length > 0) {
          formArray.removeAt(0);
        }
      });
  }

  private _onOpenChooseMbiemriDialog$: Subject<void> = new Subject<void>();

  triggerOpenChooseMbiemriDialog() {
    this._onOpenChooseMbiemriDialog$.next();
  }

  onChooseMbiemriDialogOpen() {
    return this._onOpenChooseMbiemriDialog$.asObservable();
  }
}
